<template>
  <div class="chatbox-wrapper">
    <!-- Trigger Button -->
    <div v-if="step === 'closed'" class="chatbox-trigger" @click="openChat">
      <font-awesome-icon icon="fa-solid fa-comment-dots" class="trigger-icon" />
      <span class="trigger-label">{{ $t('chat_trigger_btn') }}</span>
    </div>

    <!-- Chatbox Panel -->
    <div v-else class="chatbox-panel">
      <!-- Header -->
      <div class="chatbox-header">
        <span>{{ $t('chat_header_title') }}</span>
        <button class="chatbox-close" @click="closeChat" aria-label="close">
          <font-awesome-icon icon="fa-solid fa-xmark" />
        </button>
      </div>

      <!-- Body -->
      <div class="chatbox-body" ref="chatBody">

        <!-- Step: select-problem -->
        <template v-if="step === 'select-problem'">
          <p class="chatbox-greeting">{{ $t('chat_greeting') }}</p>
          <div class="problem-list">
            <button
              v-for="(problem, index) in problems"
              :key="index"
              class="problem-btn"
              @click="selectProblem(problem)"
            >
              {{ problem.label }}
            </button>
          </div>
        </template>

        <!-- Step: input-other (vấn đề khác) -->
        <template v-else-if="step === 'input-other'">
          <div class="chat-bubble bot">
            <p>{{ $t('chat_input_other_prompt') }}</p>
          </div>
          <div class="selected-problem-tag">
            <font-awesome-icon icon="fa-solid fa-tag" /> {{ selectedProblem.label }}
          </div>
          <textarea
            v-model="otherDescription"
            class="chatbox-textarea"
            :placeholder="$t('chat_input_other_placeholder')"
            maxlength="300"
            rows="3"
            @input="validateOther"
          ></textarea>
          <p v-if="errors.other" class="chatbox-error">{{ errors.other }}</p>
          <button class="chatbox-next-btn" :disabled="!otherDescription.trim() || !!errors.other" @click="goToInputName">
            {{ $t('chat_next') }}
          </button>
        </template>

        <!-- Step: input-name -->
        <template v-else-if="step === 'input-name'">
          <div class="selected-problem-tag">
            <font-awesome-icon icon="fa-solid fa-tag" /> {{ selectedProblem.label }}
          </div>
          <div class="chat-bubble bot">
            <p>{{ $t('chat_input_name_prompt') }}</p>
          </div>
          <input
            v-model="name"
            type="text"
            class="chatbox-input"
            :placeholder="$t('chat_input_name_placeholder')"
            maxlength="60"
            @input="validateName"
          />
          <p v-if="errors.name" class="chatbox-error">{{ errors.name }}</p>
          <button class="chatbox-next-btn" :disabled="!name.trim() || !!errors.name" @click="goToInputEmail">
            {{ $t('chat_next') }}
          </button>
        </template>

        <!-- Step: input-email -->
        <template v-else-if="step === 'input-email'">
          <div class="selected-problem-tag">
            <font-awesome-icon icon="fa-solid fa-tag" /> {{ selectedProblem.label }}
          </div>
          <div class="chat-bubble bot">
            <p>{{ $t('chat_input_email_prompt') }}</p>
          </div>
          <input
            v-model="email"
            type="email"
            class="chatbox-input"
            :placeholder="$t('chat_input_email_placeholder')"
            maxlength="100"
            @input="validateEmail"
          />
          <p v-if="errors.email" class="chatbox-error">{{ errors.email }}</p>
          <button class="chatbox-next-btn" :disabled="!email.trim() || !!errors.email" @click="goToInputPhone">
            {{ $t('chat_next') }}
          </button>
        </template>

        <!-- Step: input-phone -->
        <template v-else-if="step === 'input-phone'">
          <div class="selected-problem-tag">
            <font-awesome-icon icon="fa-solid fa-tag" /> {{ selectedProblem.label }}
          </div>
          <div class="chat-bubble bot">
            <p>{{ $t('chat_input_phone_prompt') }}</p>
          </div>
          <input
            v-model="phone"
            type="tel"
            class="chatbox-input"
            :placeholder="$t('chat_input_phone_placeholder')"
            maxlength="15"
            @input="validatePhone"
          />
          <p v-if="errors.phone" class="chatbox-error">{{ errors.phone }}</p>
          <!-- Honeypot field (hidden from real users) -->
          <input
            v-model="honeypot"
            name="website"
            type="text"
            tabindex="-1"
            autocomplete="off"
            class="chatbox-honeypot"
          />
          <button
            class="chatbox-submit-btn"
            :disabled="!phone.trim() || !!errors.phone || isSending"
            @click="submitForm"
          >
            <span v-if="isSending">
              <font-awesome-icon icon="fa-solid fa-spinner" spin /> {{ $t('chat_sending') }}
            </span>
            <span v-else>{{ $t('chat_submit') }}</span>
          </button>
          <p v-if="errors.submit" class="chatbox-error">{{ errors.submit }}</p>
        </template>

        <!-- Step: done -->
        <template v-else-if="step === 'done'">
          <div class="chatbox-done">
            <font-awesome-icon icon="fa-solid fa-circle-check" class="done-icon" />
            <p>{{ $t('chat_done') }}</p>
          </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script>
import emailjs from '@emailjs/browser';

const BLOCKED_PATTERNS = [
  /https?:\/\//i,
  /www\./i,
  /<[^>]*>/,
  /javascript:/i,
  /on\w+\s*=/i,
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/,
];

const RATE_LIMIT_MS = 60 * 1000; // 60 giây

function containsBlocked(value) {
  return BLOCKED_PATTERNS.some((pattern) => pattern.test(value));
}

export default {
  name: 'ChatBox',
  data() {
    return {
      step: 'closed', // closed | select-problem | input-other | input-name | input-email | input-phone | done
      selectedProblem: null,
      otherDescription: '',
      name: '',
      email: '',
      phone: '',
      honeypot: '',
      isSending: false,
      formOpenTime: null,
      errors: {
        other: '',
        name: '',
        email: '',
        phone: '',
        submit: '',
      },
    };
  },
  computed: {
    problems() {
      return [
        { key: 'p1', label: this.$t('chat_problem_1') },
        { key: 'p2', label: this.$t('chat_problem_2') },
        { key: 'p3', label: this.$t('chat_problem_3') },
        { key: 'p4', label: this.$t('chat_problem_4') },
        { key: 'p5', label: this.$t('chat_problem_5') },
        { key: 'other', label: this.$t('chat_problem_other') },
      ];
    },
  },
  methods: {
    openChat() {
      this.step = 'select-problem';
      this.formOpenTime = Date.now();
    },
    closeChat() {
      this.step = 'closed';
      this.resetForm();
    },
    resetForm() {
      this.selectedProblem = null;
      this.otherDescription = '';
      this.name = '';
      this.email = '';
      this.phone = '';
      this.honeypot = '';
      this.isSending = false;
      this.formOpenTime = null;
      this.errors = { other: '', name: '', email: '', phone: '', submit: '' };
    },
    selectProblem(problem) {
      this.selectedProblem = problem;
      if (problem.key === 'other') {
        this.step = 'input-other';
      } else {
        this.step = 'input-name';
      }
      this.$nextTick(() => {
        if (this.$refs.chatBody) {
          this.$refs.chatBody.scrollTop = 0;
        }
      });
    },
    validateOther() {
      if (containsBlocked(this.otherDescription)) {
        this.errors.other = this.$t('chat_error_invalid');
      } else {
        this.errors.other = '';
      }
    },
    validateName() {
      if (containsBlocked(this.name)) {
        this.errors.name = this.$t('chat_error_invalid');
      } else {
        this.errors.name = '';
      }
    },    validateEmail() {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(this.email.trim())) {
        this.errors.email = this.$t('chat_error_email');
      } else if (containsBlocked(this.email)) {
        this.errors.email = this.$t('chat_error_invalid');
      } else {
        this.errors.email = '';
      }
    },    validatePhone() {
      const phonePattern = /^[0-9+()\s-]{8,15}$/;
      if (!phonePattern.test(this.phone.trim())) {
        this.errors.phone = this.$t('chat_error_phone');
      } else {
        this.errors.phone = '';
      }
    },
    goToInputName() {
      if (!this.otherDescription.trim() || this.errors.other) return;
      this.step = 'input-name';
    },
    goToInputEmail() {
      if (!this.name.trim() || this.errors.name) return;
      this.step = 'input-email';
    },
    goToInputPhone() {
      if (!this.email.trim() || this.errors.email) return;
      this.step = 'input-phone';
    },
    canSubmit() {
      const last = localStorage.getItem('chat_last_submit');
      if (last && Date.now() - parseInt(last) < RATE_LIMIT_MS) {
        return false;
      }
      return true;
    },
    silentBlock() {
      // Giả vờ thành công để không lộ thông tin cho bot
      this.step = 'done';
    },
    async submitForm() {
      // Lớp 1: Honeypot
      if (this.honeypot !== '') {
        this.silentBlock();
        return;
      }
      // Lớp 2: Rate limit
      if (!this.canSubmit()) {
        this.errors.submit = this.$t('chat_error_rate_limit');
        return;
      }
      // Lớp 3: Time check (bot submit quá nhanh)
      if (this.formOpenTime && Date.now() - this.formOpenTime < 3000) {
        this.silentBlock();
        return;
      }
      // Lớp 4: Validate nội dung lần cuối
      if (containsBlocked(this.name) || containsBlocked(this.email) || containsBlocked(this.phone)) {
        this.errors.submit = this.$t('chat_error_invalid');
        return;
      }
      if (this.selectedProblem && this.selectedProblem.key === 'other' && containsBlocked(this.otherDescription)) {
        this.errors.submit = this.$t('chat_error_invalid');
        return;
      }

      this.isSending = true;
      this.errors.submit = '';

      // const problemText = this.selectedProblem.key === 'other'
      //   ? `${this.selectedProblem.label}: ${this.otherDescription}`
      //   : this.selectedProblem.label;

      const templateParams = {
        problem: this.selectedProblem.label,
        detail: this.otherDescription,
        name: this.name,
        email: this.email,
        phone: this.phone,
        time: new Date().toLocaleString('ja-JP'),
      };

      try {
        await emailjs.send(
          process.env.VUE_APP_EMAILJS_SERVICE_ID,
          process.env.VUE_APP_EMAILJS_TEMPLATE_ID,
          templateParams,
          process.env.VUE_APP_EMAILJS_PUBLIC_KEY
        );
        localStorage.setItem('chat_last_submit', Date.now().toString());
        this.step = 'done';
        setTimeout(() => {
          this.closeChat();
        }, 5000);
      } catch (error) {
        this.errors.submit = this.$t('chat_error_send_failed');
      } finally {
        this.isSending = false;
      }
    },
  },
};
</script>

<style scoped>
/* Trigger button animations */
@keyframes chat-slide-peek {
  0%, 70%, 100% { transform: translateX(0); }
  75%            { transform: translateX(-8px); }
  80%            { transform: translateX(-4px); }
  85%            { transform: translateX(-8px); }
  90%            { transform: translateX(-2px); }
}
@keyframes chat-glow-pulse {
  0%, 100% { box-shadow: -2px 4px 12px rgba(0,0,0,0.25), 0 0 0 0 rgba(17,28,85,0.55); }
  50%       { box-shadow: -2px 4px 12px rgba(0,0,0,0.25), 0 0 0 10px rgba(17,28,85,0); }
}
/* Trigger button */
.chatbox-trigger {
  position: fixed;
  bottom: 130px;
  right: 0;
  background-color: #111c55;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px 12px 20px;
  border-radius: 30px 0 0 30px;
  cursor: pointer;
  z-index: 1100;
  box-shadow: -2px 4px 12px rgba(0, 0, 0, 0.25);
  transition: background-color 0.2s, padding 0.2s;
  animation: chat-slide-peek 4s ease-in-out 2s infinite, chat-glow-pulse 2s ease-in-out infinite;
}
.chatbox-trigger:hover {
  background-color: #1a2a7a;
  padding-right: 22px;
  animation: none;
}
.trigger-icon {
  font-size: 1.3rem;
}
.trigger-label {
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
}

/* Panel */
.chatbox-panel {
  position: fixed;
  bottom: 80px;
  right: 20px;
  width: 340px;
  max-height: 520px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  z-index: 1100;
  overflow: hidden;
}

/* Header */
.chatbox-header {
  background: #111c55;
  color: #fff;
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 0.95rem;
}
.chatbox-close {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

/* Body */
.chatbox-body {
  padding: 16px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chatbox-greeting {
  font-size: 0.9rem;
  color: #333;
  font-weight: 600;
  margin-bottom: 6px;
}

/* Problem buttons */
.problem-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.problem-btn {
  background: #f4f6ff;
  border: 1.5px solid #c2caef;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 0.85rem;
  color: #111c55;
  cursor: pointer;
  text-align: left;
  font-weight: 500;
  transition: background 0.15s, border-color 0.15s;
}
.problem-btn:hover {
  background: #dde3f8;
  border-color: #111c55;
}
.problem-btn:last-child {
  border-style: dashed;
  color: #555;
}

/* Selected tag */
.selected-problem-tag {
  background: #e8ecfa;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.8rem;
  color: #111c55;
  font-weight: 600;
}

/* Chat bubble */
.chat-bubble.bot {
  background: #f0f2fa;
  border-radius: 0 12px 12px 12px;
  padding: 10px 14px;
  font-size: 0.88rem;
  color: #333;
}
.chat-bubble.bot p {
  margin: 0;
}

/* Inputs */
.chatbox-input,
.chatbox-textarea {
  width: 100%;
  border: 1.5px solid #c2caef;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.88rem;
  color: #333;
  outline: none;
  resize: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.chatbox-input:focus,
.chatbox-textarea:focus {
  border-color: #111c55;
}

/* Honeypot - hoàn toàn ẩn */
.chatbox-honeypot {
  position: absolute;
  left: -9999px;
  opacity: 0;
  height: 0;
  width: 0;
  pointer-events: none;
}

/* Buttons */
.chatbox-next-btn,
.chatbox-submit-btn {
  width: 100%;
  background: #111c55;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 11px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.chatbox-next-btn:hover:not(:disabled),
.chatbox-submit-btn:hover:not(:disabled) {
  background: #1a2a7a;
}
.chatbox-next-btn:disabled,
.chatbox-submit-btn:disabled {
  background: #aaa;
  cursor: not-allowed;
}

/* Error */
.chatbox-error {
  color: #c0392b;
  font-size: 0.8rem;
  margin: 0;
}

/* Done screen */
.chatbox-done {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 10px;
  gap: 16px;
  text-align: center;
}
.done-icon {
  font-size: 3rem;
  color: #27ae60;
}
.chatbox-done p {
  font-size: 0.9rem;
  color: #333;
  font-weight: 500;
  margin: 0;
}

/* Mobile */
@media (max-width: 576px) {
  .chatbox-panel {
    right: 0;
    bottom: 0;
    width: 100vw;
    max-height: 90vh;
    border-radius: 16px 16px 0 0;
  }
  .chatbox-trigger {
    bottom: 110px;
    font-size: 0.85rem;
  }
}
</style>
